import { attackIdFor } from './attack-ids'
import { newHunt, type AttackRef, type Hunt, type Methodology } from './hunt-model'

/**
 * The hunt template catalog (E6/S6.1, decision H6): a template is a
 * hypothesis with its ATT&CK mapping, methodology and starter queries —
 * enough that "new hunt from template" lands an analyst in a runnable
 * cockpit, not a blank form.
 *
 * Starter queries use PLACEHOLDER stream names in CAPS (WINDOWS-AUTH,
 * PROXY…) — every deployment names streams differently, and an honestly
 * wrong placeholder the analyst must touch beats a silently wrong guess.
 * Custom templates (save-hunt-as-template) live beside the shipped set in
 * localStorage, same house rules as the hunt store.
 */

export interface HuntTemplate {
  id: string
  name: string
  methodology: Methodology
  hypothesis: string
  attack: AttackRef[]
  /** Suggested DQL starting points, shown as chips in the cockpit. */
  starterQueries: string[]
  window: string
  /** True only for the shipped set — custom templates can be deleted. */
  builtIn?: boolean
}

const ref = (technique: string): AttackRef => ({ technique, techniqueId: attackIdFor(technique) })

export const HUNT_TEMPLATES: readonly HuntTemplate[] = [
  {
    id: 'tpl-kerberoast',
    name: 'Kerberoasting sweep',
    methodology: 'hypothesis',
    hypothesis:
      'An attacker is requesting RC4-encrypted TGS tickets for SPN-bearing service accounts to crack offline.',
    attack: [ref('Steal or Forge Kerberos Tickets')],
    starterQueries: [
      "stream=WINDOWS-AUTH where EventID = '4769' | duration 7d | limit 500",
      "stream=WINDOWS-AUTH where EventID = '4769' | duration 7d | groupby AccountName | limit 100",
    ],
    window: '7d',
    builtIn: true,
  },
  {
    id: 'tpl-beacon',
    name: 'C2 beacon detection',
    methodology: 'baseline',
    hypothesis:
      'A compromised host is beaconing to command-and-control on a fixed interval — near-constant connection counts per period to one destination.',
    attack: [ref('Application Layer Protocol')],
    starterQueries: [
      'stream=PROXY | duration 24h | groupby DstIP | limit 200',
      'stream=DNS | duration 24h | groupby Domain | limit 200',
    ],
    window: '24h',
    builtIn: true,
  },
  {
    id: 'tpl-rare-parent',
    name: 'Rare parent-child processes',
    methodology: 'baseline',
    hypothesis:
      'Malware runs under unusual parent processes — office apps or browsers spawning shells appear as rare parent/child pairs.',
    attack: [ref('Command and Scripting Interpreter')],
    starterQueries: [
      'stream=EP-PROCESS | duration 7d | groupby ParentProcessName | limit 500',
    ],
    window: '7d',
    builtIn: true,
  },
  {
    id: 'tpl-ioc-shell',
    name: 'IOC sweep shell',
    methodology: 'ioc-sweep',
    hypothesis:
      'Indicators from threat intel (hashes, IPs, domains) appear somewhere in our telemetry — sweep every relevant stream for the list.',
    attack: [ref('Indicator Removal')],
    starterQueries: [],
    window: '30d',
    builtIn: true,
  },
  {
    id: 'tpl-new-vs-known',
    name: 'New vs known values',
    methodology: 'baseline',
    hypothesis:
      'First-seen values — new user agents, new destinations, new service names — mark toeholds. Compare this window against the previous one.',
    attack: [ref('Valid Accounts')],
    starterQueries: [
      'stream=PROXY | duration 24h | groupby UserAgent | limit 500',
    ],
    window: '24h',
    builtIn: true,
  },
  {
    id: 'tpl-impossible-travel',
    name: 'Impossible travel',
    methodology: 'entity-pivot',
    hypothesis:
      'One account authenticates from locations it cannot physically span in the time between logons — credential sharing or theft.',
    attack: [ref('Valid Accounts')],
    starterQueries: [
      "stream=WINDOWS-AUTH where EventID = '4624' | duration 24h | groupby AccountName | limit 200",
    ],
    window: '24h',
    builtIn: true,
  },
  {
    id: 'tpl-lolbin',
    name: 'LOLBin abuse',
    methodology: 'hypothesis',
    hypothesis:
      'Living-off-the-land binaries (certutil, mshta, regsvr32, rundll32) are being used for download or execution outside their admin baseline.',
    attack: [ref('System Binary Proxy Execution')],
    starterQueries: [
      "stream=EP-PROCESS where ProcessName = 'certutil.exe' | duration 7d | limit 200",
      'stream=EP-PROCESS | duration 7d | groupby ProcessName | limit 500',
    ],
    window: '7d',
    builtIn: true,
  },
  {
    id: 'tpl-exfil-volume',
    name: 'Exfiltration by volume',
    methodology: 'baseline',
    hypothesis:
      'Data is leaving through an approved channel in unapproved volume — per-host outbound byte counts spike against their own history.',
    attack: [ref('Exfiltration Over Web Service')],
    starterQueries: [
      'stream=PROXY | duration 24h | groupby SrcIP sum BytesOut | limit 200',
    ],
    window: '24h',
    builtIn: true,
  },
]

/* -------------------------------------------------------- custom templates */

const customKey = (clusterId: string, scopeId: string) => `bloo.hunt-tpl.${clusterId}.${scopeId}`

export function loadCustomTemplates(clusterId: string, scopeId: string): HuntTemplate[] {
  try {
    const raw = globalThis.localStorage?.getItem(customKey(clusterId, scopeId))
    const parsed: unknown = raw ? JSON.parse(raw) : []
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (t): t is HuntTemplate =>
        typeof t === 'object' && t !== null &&
        typeof (t as HuntTemplate).id === 'string' &&
        typeof (t as HuntTemplate).name === 'string',
    )
  } catch {
    return []
  }
}

export function saveCustomTemplate(clusterId: string, scopeId: string, tpl: HuntTemplate): void {
  try {
    const cur = loadCustomTemplates(clusterId, scopeId).filter((t) => t.id !== tpl.id)
    globalThis.localStorage?.setItem(customKey(clusterId, scopeId), JSON.stringify([tpl, ...cur]))
  } catch {
    // Quota or no storage — the save is a convenience, not the record.
  }
}

export function allTemplates(clusterId: string, scopeId: string): HuntTemplate[] {
  return [...HUNT_TEMPLATES, ...loadCustomTemplates(clusterId, scopeId)]
}

export function templateById(clusterId: string, scopeId: string, id: string): HuntTemplate | null {
  return allTemplates(clusterId, scopeId).find((t) => t.id === id) ?? null
}

/* ------------------------------------------------------------- conversions */

/** A ready-to-create hunt from a template; scope streams stay the user's call. */
export function huntFromTemplate(
  tpl: HuntTemplate,
  init: { streams?: string[]; assignee?: string | null },
): Hunt {
  return newHunt({
    name: tpl.name,
    hypothesis: tpl.hypothesis,
    methodology: tpl.methodology,
    attack: tpl.attack,
    scope: { streams: init.streams ?? [], window: tpl.window },
    assignee: init.assignee ?? null,
    templateId: tpl.id,
  })
}

/** The reverse door: a lived-in hunt becomes a reusable team template. */
export function templateFromHunt(hunt: Hunt): HuntTemplate {
  return {
    id: `tpl-custom-${hunt.id}`,
    name: hunt.name,
    methodology: hunt.methodology,
    hypothesis: hunt.hypothesis,
    attack: hunt.attack,
    // The starred trail is the template's teachable core.
    starterQueries: [
      ...new Set(
        hunt.sessions.flatMap((s) => s.steps.filter((t) => t.starred && !t.pruned).map((t) => t.query)),
      ),
    ].slice(0, 5),
    window: hunt.scope.window,
  }
}
